import React, { useEffect, useRef, useState } from 'react';
import Badge from '../Badge';
import { TbCircleCheckFilled } from 'react-icons/tb';
import SlotStepProduct from './SlotStepProduct';
import { useCartContext } from '../../../Contexts/CartContext';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext'
import { useTranslation } from 'react-i18next';

interface SlotStepProps {
  product: any;
  step: any;
  className?: string;
  stepIndex: number;
  scrollToNextStepWithOffset?: CallableFunction;
}

const SlotStep = ({
  product,
  step,
  className,
  stepIndex,
  scrollToNextStepWithOffset = () => {},
}: SlotStepProps) => {

  const { t } = useTranslation();
  const { getTotalSelectedComplements, cartDisplay } = useCartContext();
  const { isStoreCounter, isMenu } = useOrderTypeContext();
  const [isRequired, setIsRequired] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isQuantityOk, setIsQuantityOk] = useState(false);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    setIsRequired(step.required && getTotalSelectedComplements(step.id) < step.minimum_quantity);
    setIsComplete(step.required && getTotalSelectedComplements(step.id) >= step.minimum_quantity);
    setIsQuantityOk(getTotalSelectedComplements(step.id) >= step.minimum_quantity);
  }, [getTotalSelectedComplements, step]);

  useEffect(() => {

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    const isQuantityValid = getTotalSelectedComplements(step.id) === step.quantity;
    const nextElement = document.getElementById(`step_${stepIndex + 1}`);

    if (!isQuantityValid || !nextElement) return;

    scrollToNextStepWithOffset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  //* tratando texto item/itens plural
  const getTextItemsPlural = (quantity: number) => {
    return quantity > 1 ? t('layout.item_label_plural') : t('layout.item_label_singular')
  }

  const getStepMessage = () => {
    const { minimum_quantity, quantity, required, type } = step;

    if (minimum_quantity && required && minimum_quantity < quantity) {
      if (type === 'multipla' && step.required) {
        return t('add_product.steps.messages.message_1_minimum_quantity', {
            minimumQuantity: minimum_quantity,
            itemsPlural: getTextItemsPlural(minimum_quantity)
        });
      } else {
          return t('add_product.steps.messages.message_1', {
            minimumQuantity: minimum_quantity,
            itemsPlural: getTextItemsPlural(minimum_quantity)
          });
      }
    }

    if (required && minimum_quantity && minimum_quantity !== quantity) {
      return t('add_product.steps.messages.message_2', {
        minimumQuantity: minimum_quantity,
        quantity: quantity
      });
    }

    if (required && minimum_quantity && minimum_quantity === quantity) {
      return t('add_product.steps.messages.message_3', {
        quantity: quantity,
        itemsPlural: getTextItemsPlural(quantity)
      });
    }

    if (!required && minimum_quantity === 0 && quantity === 1) {
      return t('add_product.steps.messages.message_4', {
        quantity: quantity,
        itemsPlural: getTextItemsPlural(quantity)
      });
    }

    if (type === 'multipla') {
        return t('add_product.steps.messages.message_5_multipla', {
            quantity: quantity,
            itemsPlural: getTextItemsPlural(quantity)
        });
      } else {
          return t('add_product.steps.messages.message_5', {
            quantity: quantity,
            itemsPlural: getTextItemsPlural(quantity)
          });
      }
  }

  return (
    <div id={`step_${stepIndex}`} className='z-50 flex bg-white flex-col md:shadow-md md:shadow-gray-100 md:ring-1 ring-gray-100 rounded-xl  md:overflow-hidden'>


      <div className='sticky top-0 z-40 flex flex-col p-4 pb-3 w-full gap-1 border-t border-gray-100 md:border-none bg-gradient-to-br from-gray-100/95 to-white/90' style={{ willChange: 'transform' }}>

          <div className='flex justify-between items-center text-left text-md gap-1.5'>
            <h4 className='text-xl text-shopfood-title font-bold leading-5'>{step.name}</h4>

            <div className='flex items-center gap-0.5 self-start'>
              {(!isStoreCounter || (isStoreCounter && cartDisplay)) && !isMenu &&
                <>
                  <Badge outline type={getTotalSelectedComplements(step.id) >= step.minimum_quantity ? 'success' : ''} className={`flex items-center justify-center text-nowrap text-xs gap-0.5 ${step.required && !isComplete && 'text-shopfood-title'}`}>
                    <span>{getTotalSelectedComplements(step.id)} / { step.quantity }</span>
                    {isComplete && <TbCircleCheckFilled size={16} className={'text-shopfood-success -mr-1'} />}
                  </Badge>
                  {isRequired && <Badge type='required' className='text-xs normal-case animate-pulse stepRequired transition-all'>{t('form.validation.required')}</Badge>}
                </>
              }
            </div>
          </div>

          {(!isStoreCounter || (isStoreCounter && cartDisplay)) &&
            <span className={`font-semibold flex gap-1 leading-5 ${isQuantityOk || !step.required ? 'text-shopfood-success' : 'text-shopfood-notify'}`}>
              { getStepMessage() }
            </span>
          }

          {step.description && <span className='text-left text-gray-500 leading-5'>{ step.description }</span>}

      </div>

      <section className={`text-left gap-1 md:gap-2 ${className} grid divide-y divide-gray-100 rounded-b-lg`}>
        {step.complements.map((complement: any, index: number) =>
          <SlotStepProduct product={product} complement={complement} step={step} key={index} />
        )}
      </section>


    </div>
  )
}

export default SlotStep;